import React, { useEffect, useMemo, useState } from 'react';
import useAutocomplete from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import _ from 'lodash';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CloseOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

const Root = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  color: ${theme[themeStyle].tag_white};
  font-size: 14px;
`,
);

const InputWrapper = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  width: 100%;
  border: 1px solid ${theme[themeStyle].tag_white};
  background-color: ${theme[themeStyle].gray_text};
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    border-color: ${theme[themeStyle].primary};
  }

  &.focused {
    border-color: ${theme[themeStyle].primary};
    box-shadow: 0 0 0 2px ${theme[themeStyle].primary};
  }

  & input {
    background-color: ${theme[themeStyle].gray_text};
    color: ${theme[themeStyle].tag_white};
    height: 50px;
    box-sizing: border-box;
    padding: 10px 12px;
    width: 0;
    min-width: 44px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    font-size: 1.1rem;
  }
`,
);

const StyledTag = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  display: flex;
  align-items: center;
  height: 36px;
  margin: 4px;
  line-height: 34px;
  background-color: ${theme[themeStyle].primary_background};
  border: 1px solid ${theme[themeStyle].tag_white};
  border-radius: 4px;
  box-sizing: content-box;
  padding: 0 10px 0 16px;
  outline: 0;
  overflow: hidden;
  font-size: 1.05rem;

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 18px;
    cursor: pointer;
    padding: 0px;
    color: ${theme[themeStyle].tag_white};
  }
`,
);

const Listbox = styled('ul')<{ themeStyle: 'dark' | 'light' }>`
    max-width: 300px;
    margin: 2px 0 0;
    padding: 0;
    position: absolute;
    list-style: none;
    background-color: ${({ themeStyle }) => theme[themeStyle].primary_background};
    overflow: auto;
    max-height: 250px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgb(0 0 0 / 0.15);
    z-index: 1;

    & li {
        padding: 5px 12px;
        display: flex;

        & span {
            flex-grow: 1;
            color: ${({ themeStyle }) => theme[themeStyle].tag_white};
        }

        & svg {
            color: transparent;
        }
    }

    & li[aria-selected='true'] {
        background-color: ${({ themeStyle }) => theme[themeStyle].primary};
        font-weight: 600;

        & svg {
            color: ${({ themeStyle }) => theme[themeStyle].tag_white};
        }
    }

    & li.${autocompleteClasses.focused} {
        background-color: ${({ themeStyle }) => theme[themeStyle].highlight};
        cursor: pointer;

        & svg {
            color: currentColor;
        }
    }
`;

interface AutocompleteProps {
    options: string[];
    defaultValue?: string[];
    onChange?: (value: string[]) => void;
    maxSelectable?: number;
    initialValue: string[];
}

const CustomizedAutocomplete: React.FC<AutocompleteProps> = ({
    options = [],
    defaultValue = [],
    onChange,
    maxSelectable,
    initialValue = [],
}) => {
    const safeInitialValue = useMemo(() => (Array.isArray(initialValue) ? initialValue : []), [initialValue]);
    const [initValue, setInitialValue] = useState<string[]>(safeInitialValue);
    const { themeStyle } = useThemeContext();

    const {
        getRootProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value = [], // Ensure value is an array
        focused,
        setAnchorEl,
    } = useAutocomplete({
        multiple: true,
        options,
        defaultValue,
        value: initValue, // Ensure value is tied to state
        isOptionEqualToValue: (option, val) => option === val && val !== undefined, // Ensure correct value matching
        onChange: (_, newValue) => {
            if (maxSelectable && newValue.length > maxSelectable) return;
            setInitialValue(Array.isArray(newValue) ? newValue : []);
            onChange?.(Array.isArray(newValue) ? newValue : []);
        },
    });

    // Ensure initialValue syncs properly without unnecessary updates
    useEffect(() => {
        if (!_.isEqual(initValue, safeInitialValue)) {
            setInitialValue(safeInitialValue);
        }
    }, [safeInitialValue, initValue]);

    // Ensure value syncs properly without unnecessary updates
    useEffect(() => {
        if (!_.isEqual(initValue, value) && value) {
            setInitialValue(Array.isArray(value) ? value : []);
        }
    }, [value, initValue]);

    return (
        <Root themeStyle={themeStyle}>
            <div {...getRootProps()}>
                <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} themeStyle={themeStyle}>
                    {initValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <StyledTag key={key} themeStyle={themeStyle}>
                                <span>{option}</span>
                                <CloseOutlined
                                    onClick={tagProps.onDelete}
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                    }}
                                />
                            </StyledTag>
                        );
                    })}
                    <input {...getInputProps()} />
                </InputWrapper>
                <Box sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 8,
                            color: theme[themeStyle].gray_message,
                            fontStyle: 'italic',
                            mt: 2,
                        }}>
                        Select {maxSelectable} max.
                    </Typography>
                </Box>
            </div>
            {groupedOptions.length > 0 && (
                <Listbox sx={{ zIndex: 99999 }} {...getListboxProps()} themeStyle={themeStyle}>
                    {groupedOptions.map((option, index) => {
                        const optionLabel: any =
                            typeof option === 'string' ? option : 'label' in option ? option.label : option;
                        const { ...optionProps } = getOptionProps({ option: optionLabel, index });

                        return (
                            <li
                                key={index}
                                {...optionProps}
                                style={{
                                    pointerEvents: maxSelectable && initValue.length >= maxSelectable ? 'none' : 'auto',
                                    opacity: maxSelectable && initValue.length >= maxSelectable ? 0.5 : 1,
                                }}>
                                <span
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        textTransform: 'capitalize',
                                    }}>
                                    {optionLabel}
                                </span>
                                <CheckIcon fontSize="small" />
                            </li>
                        );
                    })}
                </Listbox>
            )}
        </Root>
    );
};

export default CustomizedAutocomplete;
